import { SimpleDocCommand } from '../../DocsApp/commands/v5/types';
import { CommandExecutionContext } from '../../../types/commands';
import fetchService from '../../../services/fetchService';
import { API_BASE_URL } from '../../../api/config';
import { schemaToParameters } from '../../../utils/schemaToParameters';

export const contacts_create: SimpleDocCommand = {
  id: 'contacts_create',
  logicalName: 'contacts_create',
  version: 'v1',

  description: `Create a new Contact entity in the user's address book.

**EXAMPLE:**
{ "name": "Alice Chen", "email": "alice@example.com", "company": "Acme", "tier": "close" }

**RETURNS:** Created contact with id, name, and llmId.`,

  schema: {
    parameters: schemaToParameters({
      type: 'object',
      properties: {
        name:          { type: 'string', description: 'Full name. Required.' },
        email:         { type: 'string', description: 'Email address' },
        phone:         { type: 'string', description: 'Phone number' },
        company:       { type: 'string', description: 'Company or organization' },
        title:         { type: 'string', description: 'Job title or role' },
        tier: {
          type: 'string',
          enum: ['close', 'regular', 'archived'],
          description: 'Relationship tier (default: regular)',
        },
        notes:         { type: 'string', description: 'Initial notes about this contact' },
        tags:          { type: 'array', items: { type: 'string' }, description: 'Tags (e.g. ["linkedin", "reddit"])' },
        linkedinUrl:   { type: 'string', description: 'LinkedIn profile URL' },
        twitterHandle: { type: 'string', description: 'Twitter/X handle (without @)' },
        sources:       { type: 'array', items: { type: 'string' }, description: 'Data sources where contact was found (e.g. ["gmail", "reddit"])' },
      },
      required: ['name'],
    }),
  },

  handler: async (params: any, context: CommandExecutionContext) => {
    const {
      name, email, phone, company, title,
      tier = 'regular', notes, tags, linkedinUrl, twitterHandle,
      redditHandle, sources, primaryChannel, primaryChannelContext,
      identityType,
    } = params;

    if (!name?.trim()) {
      return { status: 'error', message: 'name is required', format: 'plaintext' as const };
    }

    // Use find-or-create so agents never produce duplicate contacts.
    const contact = await fetchService(`${API_BASE_URL}/contacts-app/find-or-create`, {
      method: 'POST',
      body: JSON.stringify({
        name, email, phone, company, title, tier, notes, tags,
        linkedinUrl, twitterHandle, redditHandle, sources,
        primaryChannel, primaryChannelContext, identityType,
      }),
    });

    return {
      status: 'success' as const,
      message: contact._found
        ? `Found existing contact: ${contact.name}`
        : `Contact created: ${name}`,
      data: {
        contactId: contact._id,
        llmId: contact.llmId,
        name: contact.name,
        tier: contact.skeleton?.tier ?? tier,
        handles: contact.skeleton?.handles,
        found: contact._found ?? false,
      },
      format: 'json' as const,
    };
  },
};
